/**
 * @file src/world/sceneProfileController.js
 * @description Applies the performance-mode preference to ONE voice's 3D.
 *
 * The mode does not draw a cheaper scene — it stops drawing. Measurement on the machine the mode
 * exists for settled that: a plain sphere and basic moons at pixel ratio 1 still ran below the frame
 * cap, and backgrounding the tab, where the browser stops calling rAF at all, cleaned the audio
 * instantly. The cost is rendering, not what is rendered.
 *
 * So this module is the one owner of "give the 3D up" and "build it again". It sequences the two
 * halves that must not be reordered: the scene's CONTENTS (whoever built them) and the RENDERER
 * (`WorldSceneController`). Going off, the contents are released first and the context second, so
 * three.js frees GPU resources through a context that is still alive. Coming back, the context is
 * built first and the contents second, so textures and geometry bind to the context that will draw
 * them. Doing either in the other order leaks or binds to a dead context.
 *
 * The rule it enforces: this RELEASES, it does not hide. Making things invisible would leave the GPU
 * memory and the per-frame work exactly where they were, which is the cost this mode exists to remove.
 *
 * What the mode puts in the emptied centre — the world's DNA figure — is sequenced from here too,
 * for the same reason the two halves above are: it and the planet occupy the same space, so exactly
 * one of them may be up at any moment, and only the thing that owns the switch can promise that.
 *
 * Audio is never touched here. Not the engine, not the analysers that feed it, not the quality
 * profile — only the analyser taps that exist purely to draw something.
 */
import { GRAPHICS_PRESETS } from '../config/performance.js';
import { applySpaceBackground } from './SceneSpace.js';
import {
  destroyOscilloscopeOverlay,
  ensureOscilloscopeOverlay,
  updateOscilloscopePerformanceProfile,
} from './Scene.js';

/**
 * @param {{
 *   worldController: object,
 *   scene: object,
 *   oscilloscope: object,
 *   monitorHydration: { setIntervalMs(ms: number): void },
 *   getGraphicsProfile: () => object,
 *   shedWorld?: () => Promise<void> | void,
 *   rebuildWorld?: () => Promise<void> | void,
 *   setFigureVisible?: (visible: boolean) => void,
 * }} deps
 */
export function createSceneProfileController({
  worldController,
  scene,
  oscilloscope,
  monitorHydration,
  getGraphicsProfile,
  shedWorld,
  rebuildWorld,
  setFigureVisible,
}) {
  let applied = null;
  /**
   * Whether the ring was on before the 3D was given up, so coming back restores THAT rather than a
   * default. It is a persisted per-orbiter design choice: asserting "ring on" would hand a ring to a
   * designer who never wanted one.
   *
   * The world's LIGHTS need no such treatment. Rebuilding re-applies the world's own authored
   * lighting on the way back, so there is nothing to remember — the look that returns is the one the
   * world describes, not one this module carried across.
   */
  let ringWasEnabled = true;
  /**
   * Toggles are user gestures and can arrive faster than a rebuild completes. Each apply takes a
   * ticket; a slow half that finds itself superseded stops rather than finishing into a scene that
   * has already moved on — otherwise an off→on→off burst could end with the 3D rebuilt into a mode
   * that is supposed to have none.
   */
  let generation = 0;

  /**
   * Who switches the renderer. A lone orbiter owns its own, so this seam switches it. A voice in a
   * realm shares ONE renderer with its siblings, and the realm switches that for all of them at once
   * — a voice doing it here would be destroying or rebuilding its siblings' renderer as well as its
   * own. Either way this seam still owns THIS voice's scene contents; only the renderer differs.
   */
  const ownsRendererLifecycle = () => !worldController?.isRealmVoice;

  /**
   * Give up the 3D, or build it back. Idempotent: re-applying the current state does nothing, so a
   * preference event that did not actually change anything costs nothing.
   *
   * @param {boolean} off true to stop the 3D entirely
   * @returns {Promise<boolean>} whether anything changed
   */
  async function apply(off) {
    const next = Boolean(off);
    if (applied === next) return false;
    applied = next;
    const ticket = ++generation;

    if (next) {
      ringWasEnabled = oscilloscope?.enabled !== false;
      // Two steps, and both matter. `setEnabled(false)` disposes the orbit line's geometry and
      // material AND flips the flag the per-frame path reads, so nothing computes an amplitude for a
      // ring that is not there. `destroy()` then takes the now-empty group out of the scene.
      oscilloscope?.setEnabled?.(false);
      destroyOscilloscopeOverlay(oscilloscope);
      // The skybox is six network-loaded textures — by far the largest thing the scene holds, and
      // with the 3D off it is neither drawn nor kept. Taking it down is synchronous, so it happens
      // here rather than being awaited: the renderer these textures belong to is still standing.
      void applySpaceBackground(scene, { enabled: false });
      // Monitor hydration is DOM work on the main thread, driven by the same audio the engine is
      // trying to keep smooth. The cheapest cadence, regardless of graphics quality.
      monitorHydration?.setIntervalMs?.(GRAPHICS_PRESETS.LOW.uiMonitorThrottleMs);

      // Contents first, context second — see the file comment. `shedWorld` disposes the planet and
      // the moons through the renderer that is still standing.
      await shedWorld?.();
      if (ticket !== generation) return true;
      if (ownsRendererLifecycle()) worldController?.setSceneProfile?.('off');
      // LAST, once the 3D is genuinely gone. What the mode leaves in the centre is not a substitute
      // scene running beside the real one — it is what fills the space the released renderer left,
      // so it is put there after the release rather than crossfaded with it.
      setFigureVisible?.(true);
      return true;
    }

    // FIRST on the way back, before anything is rebuilt: the planet is about to occupy this space
    // again and the two must never be drawn at once. Taking the drawing away is synchronous and
    // costs nothing, so there is no window where both are up.
    setFigureVisible?.(false);

    // Context first, contents second. The moons bind their compressed textures to a renderer at
    // build time, so the renderer has to be the one that will draw them.
    if (ownsRendererLifecycle()) worldController?.setSceneProfile?.('full');
    // Rebuilt from the user's real graphics quality, not from whatever the mode was using.
    const profile = getGraphicsProfile?.();
    // Not awaited: the faces come off the network, and the ring, the monitor cadence and the world
    // rebuild have no reason to queue behind them.
    //
    // The renderer is READ here rather than held. A lone orbiter's was built one line ago; a realm
    // voice's belongs to the realm and is replaced whenever the realm rebuilds its compositor. The
    // KTX2 transcoder binds to whichever it is handed, so it has to be the current one.
    //
    // Only LOW asserts the small faces. Anything else leaves it undefined, which is the shared
    // loader's cue to judge the device — the judgement that replaced this repo's viewport check.
    void applySpaceBackground(scene, {
      enabled: true,
      renderer: worldController?.renderer ?? null,
      forceLow: profile?.key === 'LOW' ? true : undefined,
    });

    // The ring comes back only if it was on. It is a saved per-orbiter design choice, not a default.
    if (ringWasEnabled) {
      oscilloscope?.setEnabled?.(true);
      ensureOscilloscopeOverlay(oscilloscope, scene);
    }
    if (profile) updateOscilloscopePerformanceProfile(oscilloscope, profile);
    monitorHydration?.setIntervalMs?.(profile?.uiMonitorThrottleMs ?? 0);

    // Checked on this path too, not only on the way out. Toggle on and off again before the world
    // finishes loading and this rebuild would otherwise still be inside `setMode('play')`, decoding
    // textures and binding moons to a renderer the second switch has already destroyed.
    if (ticket !== generation) return true;
    await rebuildWorld?.();

    // And checked AFTER, because the rebuild is the long half. A switch back off can land while
    // `setMode('play')` is still decoding textures: it sheds a world that is not there yet, gives up
    // the renderer and puts the figure up, and then THIS line resolves and puts the world back. The
    // mode would be off, the figure drawn over the top, and a whole scene rendering underneath it —
    // paying every frame of the cost the mode exists to remove, where nobody can see it. A realm
    // voice makes it worse: it does not own the renderer, so nothing else would ever stop the loop.
    // Shedding here is the only place that can undo a rebuild that finished too late.
    if (ticket !== generation) await shedWorld?.();
    return true;
  }

  return {
    apply,
    /** Whether the 3D is currently given up, or null before the first apply. */
    get is3dOff() {
      return applied === true;
    },
  };
}
